import { makeStyles } from "@mui/styles";

const themeTokens = {
  primary: "var(--brand-primary)",
  primaryHover: "var(--brand-primary-hover)",
  secondary: "var(--brand-secondary)",
  lightAccent: "var(--brand-accent)",
  info: "var(--brand-info)",
  darkPrimary: "var(--brand-dark)",
  navy: "var(--brand-navy)",
  textPrimary: "var(--text-primary)",
  textSecondary: "var(--text-secondary)",
  background: "var(--bg-page)",
  surface: "var(--surface)",
};

export const LoginStyle = makeStyles(() => ({
  loginContainer: {
    minHeight: "100vh",
    display: "flex",
    position: "relative",
    overflow: "hidden",
    background: `linear-gradient(135deg, ${themeTokens.navy} 0%, ${themeTokens.darkPrimary} 50%, ${themeTokens.primary} 100%)`,
    fontFamily: "'Outfit', 'Inter', 'Roboto', 'Helvetica', sans-serif",
  },

  backgroundBlob1: {
    position: "absolute",
    width: 600,
    height: 600,
    borderRadius: "50%",
    background: "rgba(8, 96, 211, 0.16)",
    filter: "blur(160px)",
    top: -150,
    left: -150,
    pointerEvents: "none",
  },

  backgroundBlob2: {
    position: "absolute",
    width: 550,
    height: 550,
    borderRadius: "50%",
    background: "rgba(162, 205, 240, 0.24)",
    filter: "blur(170px)",
    bottom: -150,
    right: -120,
    pointerEvents: "none",
  },

  particleLayer: {
    position: "absolute",
    inset: 0,
    backgroundImage: `radial-gradient(rgba(32, 141, 233, 0.1) 1px, transparent 1px)`,
    backgroundSize: "40px 40px",
    pointerEvents: "none",
  },

  leftPanel: {
    width: "55%",
    padding: "60px 60px 40px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    position: "relative",
    zIndex: 2,
    color: themeTokens.surface,
  },

  brand: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    color: themeTokens.primary,
    fontSize: 13,
    letterSpacing: 2,
    fontWeight: 700,
    textTransform: "uppercase",
  },

  brandDivider: {
    width: 32,
    height: 2,
    background: `linear-gradient(90deg, ${themeTokens.primary} 0%, ${themeTokens.secondary} 100%)`,
    borderRadius: 2,
    display: "inline-block",
  },

  heading: {
    fontSize: 48,
    fontWeight: 800,
    lineHeight: 1.12,
    marginBottom: 10,
    letterSpacing: -1,
    color: themeTokens.surface,
    "& span": {
      background: `linear-gradient(90deg, ${themeTokens.primaryHover} 0%, ${themeTokens.secondary} 100%)`,
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
    },
  },

  description: {
    fontSize: 18,
    color: themeTokens.textSecondary,
    lineHeight: 1.8,
    maxWidth: 520,
  },

  featureGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
    marginBottom: 44,
  },

  featureCard: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "18px 20px",
    borderRadius: 18,
    background: "rgba(255,255,255,0.72)",
    border: `1px solid var(--divider)`,
    backdropFilter: "blur(20px)",
    transition: "background .25s, border-color .25s",
    "&:hover": {
      background: "rgba(255,255,255,0.9)",
      borderColor: themeTokens.primaryHover,
    },
    "& h4": {
      margin: 0,
      marginBottom: 4,
      color: themeTokens.textPrimary,
      fontSize: 14,
      fontWeight: 600,
    },
    "& span": {
      color: themeTokens.textSecondary,
      fontSize: 13,
    },
  },

  featureIcon: {
    fontSize: 26,
    flexShrink: 0,
    color: themeTokens.primary,
  },

  stats: {
    display: "flex",
    gap: 50,
    "& h3": {
      fontSize: 32,
      margin: "0 0 4px",
      fontWeight: 800,
      color: themeTokens.primary,
    },
    "& span": {
      color: themeTokens.textSecondary,
      fontSize: 13,
    },
  },

  footer: {
    color: themeTokens.textSecondary,
    fontSize: 13,
    marginTop: 40,
    "& a": {
      color: themeTokens.primary,
      textDecoration: "none",
    },
  },

  rightPanel: {
    width: "45%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    zIndex: 2,
    padding: "40px 0",
  },

  loginCard: {
    width: 460,
    padding: "42px 40px 36px",
    borderRadius: 28,
    background: themeTokens.surface,
    backdropFilter: "blur(40px)",
    border: `1px solid var(--divider)`,
    boxShadow: "0 24px 60px rgba(2, 6, 23, 0.08)",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
  },

  logoWrap: {
    display: "flex",
    justifyContent: "center",
  },

  logoBox: {
    width: 64,
    height: 64,
    borderRadius: 18,
    background: `linear-gradient(135deg, ${themeTokens.primary} 0%, ${themeTokens.secondary} 100%)`,
    boxShadow: "0 10px 24px rgba(8, 96, 211, 0.16)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 28,
    color: themeTokens.surface,
    fontWeight: 900,
    letterSpacing: -1,
    border: "1.5px solid rgba(255,255,255,0.45)",
  },

  welcomeTitle: {
    textAlign: "center",
    color: themeTokens.textPrimary,
    fontSize: 32,
    fontWeight: 800,
    margin: "0 0 8px",
    letterSpacing: -0.5,
  },

  subtitle: {
    textAlign: "center",
    color: themeTokens.textSecondary,
    margin: "0 0 28px",
    fontSize: 15,
    fontWeight: 400,
  },

  fieldLabel: {
    color: themeTokens.textPrimary,
    fontSize: 14,
    fontWeight: 500,
    marginBottom: 8,
    display: "block",
  },

  inputWrap: {
    position: "relative",
    marginBottom: 18,
  },

  inputIcon: {
    position: "absolute",
    left: 16,
    top: "50%",
    transform: "translateY(-50%)",
    color: themeTokens.textSecondary,
    fontSize: 18,
    pointerEvents: "none",
    display: "flex",
    alignItems: "center",
  },

  eyeIcon: {
    position: "absolute",
    right: 16,
    top: "50%",
    transform: "translateY(-50%)",
    color: themeTokens.textSecondary,
    fontSize: 18,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    background: "none",
    border: "none",
    padding: 0,
    "&:hover": { color: themeTokens.primary },
  },

  input: {
    width: "100%",
    height: 52,
    padding: "0 48px 0 46px",
    borderRadius: 14,
    background: themeTokens.background,
    border: `1.5px solid var(--divider)`,
    color: themeTokens.textPrimary,
    fontSize: 15,
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color .2s, box-shadow .2s",
    "&::placeholder": {
      color: themeTokens.textSecondary,
    },
    "&:focus": {
      borderColor: themeTokens.primaryHover,
      boxShadow: `0 0 0 4px rgba(32, 141, 233, 0.16)`,
    },
  },

  inlineError: {
    color: "var(--state-danger)",
    fontSize: 12,
    marginTop: -12,
    marginBottom: 12,
    paddingLeft: 4,
    fontWeight: 500,
  },

  rememberRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 22,
    "& label": {
      display: "flex",
      alignItems: "center",
      gap: 8,
      color: themeTokens.textSecondary,
      fontSize: 14,
      cursor: "pointer",
    },
    "& input[type='checkbox']": {
      accentColor: themeTokens.primary,
      width: 15,
      height: 15,
      cursor: "pointer",
    },
    "& a": {
      color: themeTokens.primary,
      fontSize: 14,
      fontWeight: 600,
      textDecoration: "none",
      "&:hover": { textDecoration: "underline" },
    },
  },

  loginButton: {
    width: "100%",
    height: 54,
    border: "none",
    borderRadius: 14,
    cursor: "pointer",
    color: themeTokens.surface,
    fontSize: 17,
    fontWeight: 700,
    letterSpacing: 0.3,
    background: `linear-gradient(90deg, ${themeTokens.primary} 0%, ${themeTokens.primaryHover} 100%)`,
    transition: "transform .2s, box-shadow .2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: `0 16px 40px rgba(8, 96, 211, 0.2)`,
    },
    "&:disabled": {
      opacity: 0.6,
      cursor: "not-allowed",
      transform: "none",
    },
  },

  dividerRow: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    margin: "20px 0",
    "& span": {
      color: themeTokens.textSecondary,
      fontSize: 13,
    },
  },

  dividerLine: {
    flex: 1,
    height: 1,
    background: "var(--divider)",
  },

  googleButton: {
    width: "100%",
    height: 52,
    borderRadius: 14,
    background: themeTokens.background,
    border: `1.5px solid var(--divider)`,
    color: themeTokens.textPrimary,
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    transition: "background .2s, border-color .2s, transform .2s",
    "&:hover": {
      background: themeTokens.surface,
      borderColor: themeTokens.primaryHover,
      transform: "translateY(-1px)",
    },
  },

  googleIcon: {
    width: 20,
    height: 20,
  },

  versionText: {
    textAlign: "center",
    color: themeTokens.textSecondary,
    fontSize: 12,
    marginTop: 22,
  },

  securityText: {
    display: "none",
  },

  "@media(max-width:1100px)": {
    leftPanel: { display: "none" },
    rightPanel: { width: "100%" },
    loginCard: { width: "90%", maxWidth: 460 },
  },
}));
